import Modal from '../modal/modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import chunk from 'lodash.chunk';
import isEmpty from 'lodash.isempty';
import cloneDeep from 'lodash.clonedeep';
import classNames from 'classnames';
import ErrorMessage from '../error-message/error-message';
import { useFocus } from '../hooks';
import { NextLevelKey, OrgTreeItem, Company } from '@employ-engage/data';
import slugify from 'slugify';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_CURRENT_COMPANY } from '../api';

export interface AddOrgModalProps {
  company: Company;
  completeOrgTree: OrgTreeItem;
  path: (string | number)[];
  open: boolean;
  setOpen: (open: boolean) => void;
  orgType: string;
  itemLabel: string;
  nextLevelSubKeys: NextLevelKey[];
  setCollapseTree: (collapse: boolean) => void;
  refetchCompany: () => void;
}

export function AddOrgModal({
  completeOrgTree,
  path,
  open,
  setOpen,
  orgType,
  itemLabel,
  nextLevelSubKeys,
  setCollapseTree,
  refetchCompany,
}: AddOrgModalProps) {
  const { t } = useTranslation(['common']);
  const focusRef = useFocus();
  const [updateCompany] = useMutation(UPDATE_CURRENT_COMPANY);

  const {
    handleSubmit,
    values,
    errors,
    handleChange,
    touched,
    handleBlur,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
    },
    onSubmit: (values, { resetForm }) => {
      const buildOrgTree: any = (
        nextLevelSubKeys: NextLevelKey[],
        values: any
      ) => {
        const relevantSubKeys = nextLevelSubKeys.slice(1);
        const clonedOrgTree = cloneDeep(completeOrgTree);
        let ref: any | [] = clonedOrgTree;
        const lastSubKey = relevantSubKeys[relevantSubKeys.length - 1];

        if (isEmpty(path)) {
          if (!ref[lastSubKey.id]) {
            ref[lastSubKey.id] = [values];
          } else {
            ref[lastSubKey.id].push(values);
          }
        } else {
          chunk(path, 2).forEach((item, index, chunks) => {
            const org = item[0];
            const arrayIndex = item[1];

            if (index === chunks.length - 1) {
              const destinationArray = ref[org][arrayIndex][lastSubKey.id];

              if (!destinationArray) {
                ref[org][arrayIndex][lastSubKey.id] = [values];
              } else {
                ref[org][arrayIndex][lastSubKey.id].push(values);
              }
            } else {
              ref = ref[org][arrayIndex];
            }
          });
        }

        return clonedOrgTree;
      };
      setCollapseTree(false);

      const clonedOrgTree = buildOrgTree(nextLevelSubKeys, values);

      updateCompany({
        variables: {
          input: {
            orgTree: clonedOrgTree,
          },
        },
      })
        .then(() => {
          resetForm();
          refetchCompany();
          setOpen(false);
        })
        .catch((error) => console.error(error));
    },
    validationSchema: Yup.object().shape({
      id: Yup.string(),
      name: Yup.string().required(t('Required')),
    }),
  });

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <form onSubmit={handleSubmit}>
        {itemLabel && <h2 className="text-lg font-bold">{itemLabel}</h2>}
        <h2 className="text-md font-bold">{t(orgType)}</h2>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-full">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-light-primaryText dark:text-dark-primaryText"
            >
              {t('Name')}
            </label>
            <div className="mt-2">
              <input
                ref={focusRef}
                id="name"
                name="name"
                type="text"
                value={values.name}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue(
                    'id',
                    slugify(e.target.value, {
                      replacement: '-',
                      lower: true,
                      strict: true,
                    })
                  );
                }}
                onBlur={handleBlur}
                className="org-name-input text-light-primaryText dark:text-light-primaryText focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <ErrorMessage errors={errors.name} touched={touched.name} />
          </div>
        </div>
        <div className="mt-8 flex">
          <button
            type="submit"
            className={classNames(
              'add-org-name-button',
              'w-full rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500',
              {
                'opacity-50 cursor-not-allowed': !isEmpty(errors),
              }
            )}
            disabled={!isEmpty(errors)}
          >
            {t('Add')} {t(orgType)}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AddOrgModal;
